// pages/ExpensePurchaseOrder/SnEpoAwaitingFlApproval.js
//
// Legacy menu: "SN-PO Awaiting FL Approval" — under EPO.
// Legacy form: Projects/snEpoApproval.vb / epoVote.vb
//
// R42 — replaced the single-input search toolbar with the column-filter
// row pattern (consistent with R41 across the rest of EPO).
//
// Access: MPC / SuperUser / IT Admin.
// Calls API: GET /api/epo/sn/awaiting-fl  (+ tab variants)

import React, { useEffect, useState, useMemo } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Card, CardBody, Badge, Alert,
    Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import classnames from "classnames";
import {
    DashboardPageHeader,
    EmptyState,
    TableSkeleton,
    ColumnFilterRow,
    applyColumnFilters,
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};

const TABS = [
    { id: "fl",    label: "Awaiting FL Approval", url: "epo/sn/awaiting-fl"    },
    { id: "all",   label: "All Pending SN-EPOs",  url: "epo/sn/awaiting-all"   },
    { id: "mine",  label: "Awaiting My Vote",     url: "epo/sn/awaiting-mine"  },
];

const SnEpoAwaitingFlApproval = () => {
    const navigate = useNavigate();
    const { isMPC, isSuperUser, isItAdmin } = useAuth() || {};
    const canSee = isMPC || isSuperUser || isItAdmin;

    const [activeTab, setActiveTab] = useState("fl");
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);

    // R42 — per-column filters.
    const [columnFilters, setColumnFilters] = useState({});

    useEffect(() => {
        if (!canSee) { setLoading(false); return; }
        let cancelled = false;
        const tab = TABS.find((t) => t.id === activeTab) || TABS[0];
        setLoading(true);
        setError(null);
        // R42 — switching tab resets filters (matches MpcEpoViewer R41 behavior).
        setColumnFilters({});
        api.get(tab.url)
           .then((res) => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []); })
           .catch((e)   => { if (!cancelled) setError(e?.response?.data?.error || e.message || "Failed to load."); })
           .finally(()  => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, canSee]);

    // R42 — column definitions.
    const columns = useMemo(() => [
        { id: "poNum",     label: "PO #",        type: "text",
          get: r => r.poNum },
        { id: "vendor",    label: "Vendor",      type: "text",
          get: r => r.vendor },
        { id: "createdBy", label: "Created By",  type: "text",
          get: r => r.createdBy },
        { id: "total",     label: "Total",       type: "text",
          get: r => r.total != null ? String(r.total) : "" },
        { id: "purpose",   label: "Purpose",     type: "text",
          get: r => r.purpose },
        { id: "created",   label: "Created",     type: "text",
          get: r => fmtDate(r.created) },
        { id: "status",    label: "Status",      type: "select",
          options: [
            { value: "PENDING",  label: "Pending"  },
            { value: "APPROVED", label: "Approved" },
            { value: "DENIED",   label: "Denied"   },
          ],
          get: r => String(r.status || "PENDING").toUpperCase() },
    ], []);

    const visibleRowsBase = useMemo(
        () => applyColumnFilters(rows, columns, columnFilters),
        [rows, columns, columnFilters]
    );

    // Round 30 — click-to-sort (type-aware) on the loaded rows.
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(visibleRowsBase, columns);
    if (!canSee) {
        return (
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader title="SN-PO Awaiting FL Approval" breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "SN-PO Awaiting FL Approval" }]} />
                    <Alert color="warning">
                        This page is restricted to MPC / Super User / IT Admin roles.
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader title="SN-PO Awaiting FL Approval" breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "SN-PO Awaiting FL Approval" }]} />

                <Card>
                    <CardBody>
                        <Nav tabs className="mb-3">
                            {TABS.map((t) => (
                                <NavItem key={t.id}>
                                    <NavLink
                                        className={classnames({ active: activeTab === t.id })}
                                        onClick={() => setActiveTab(t.id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {t.label}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId={activeTab}>
                                {loading ? (
                                    <div className="table-responsive epo-sticky-head">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                <TableSkeleton rows={6} columns={columns.length} />
                                            </tbody>
                                        </table>
                                    </div>
                                ) : error ? (
                                    <Alert color="danger">{error}</Alert>
                                ) : rows.length === 0 ? (
                                    <EmptyState
                                        title="No SN-EPOs in this queue"
                                        description="Nothing currently needs SN/FL approval."
                                    />
                                ) : (
                                    <>
                                                                        {/* Round 29 — CSV of the full list (client-side data, respects filters) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" onClick={() => exportRowsToCsv("sn-epos-awaiting-fl.csv", columns.filter((c) => c.type !== "none"), rows)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({(rows).length})
                                    </button>
                                </div>
<div className="table-responsive epo-sticky-head">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        {columns.map((c) => <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />)}
                                                    </tr>
                                                    <ColumnFilterRow
                                                        columns={columns}
                                                        filters={columnFilters}
                                                        onChange={setColumnFilters}
                                                    />
                                                </thead>
                                                <tbody>
                                                    {visibleRows.map((r) => (
                                                        <tr
                                                            key={r.poNum}
                                                            onClick={() => navigate(`/epo/${r.poNum}/timeline`)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <td>{r.poNum}</td>
                                                            <td>{r.vendor || "—"}</td>
                                                            <td>{r.createdBy || "—"}</td>
                                                            <td>{fmtMoney(r.total)}</td>
                                                            <td>
                                                                <span style={{ display: "inline-block", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                                                                    {r.purpose || "—"}
                                                                </span>
                                                            </td>
                                                            <td>{fmtDate(r.created)}</td>
                                                            <td>
                                                                <Badge color="warning" pill style={{ fontSize: 11 }}>
                                                                    {r.status || "PENDING"}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {visibleRows.length === 0 && (
                                            <div className="text-muted text-center py-3 small">
                                                No rows match the current filters.
                                            </div>
                                        )}
                                    </>
                                )}
                            </TabPane>
                        </TabContent>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default SnEpoAwaitingFlApproval;
